import { useFetchPendingSubscriptionsQuery } from "../../redux/queries"
import { selectUserDetails } from "../../redux/slices/auth"
import { MAX_DREAM_RATE_COUNT } from "../constants/dream-rate"
import { useAppSelector } from "./use-store"

type UsePendingSubscriptionsCountOptions = {
  // Optional override to customize the fetch arg (e.g., brand/org filters)
  queryArgs?: Record<string, any>
  // Whether to limit the query to just detect up to the limit (default: true)
  limitToMax?: boolean
}

type UsePendingSubscriptionsCountReturn = {
  count: number
  isFetching: boolean
  data: any[]
  maxReached: boolean
  isDreamRateActive: boolean
}

/**
 * Custom hook for fetching pending subscriptions count
 *
 * @param options - Configuration options for the hook
 * @returns Object containing count, isFetching state, maxReached flag, and raw data
 *
 * @example
 * ```tsx
 * const { count, isFetching, maxReached } = usePendingSubscriptionsCount()
 * const { count, isFetching, maxReached } = usePendingSubscriptionsCount({
 *   queryArgs: { orgId: '123' }
 * })
 * ```
 */
export const usePendingSubscriptionsCount = (
  options: UsePendingSubscriptionsCountOptions = {}
): UsePendingSubscriptionsCountReturn => {
  const { queryArgs = {}, limitToMax = true } = options

  const userDetails = useAppSelector(selectUserDetails)
  const loid = userDetails?.id
  const orgId = userDetails?.org?.id
  const isDreamRateActive = userDetails?.org?.dream_rate_active ?? false

  // Keep the query small; we only need to detect up to the limit
  let finalQueryArgs = limitToMax
    ? { ...queryArgs, limit: MAX_DREAM_RATE_COUNT }
    : { ...queryArgs }

  // Only add loid/orgId if not present on queryArgs
  if (
    typeof (finalQueryArgs as Record<string, any>).loid === "undefined" &&
    typeof loid !== "undefined"
  ) {
    finalQueryArgs = { ...finalQueryArgs, loid }
  }
  if (
    typeof (finalQueryArgs as Record<string, any>).org_id === "undefined" &&
    typeof orgId !== "undefined"
  ) {
    finalQueryArgs = { ...finalQueryArgs, org_id: orgId }
  }

  const { data = [], isFetching } = useFetchPendingSubscriptionsQuery(
    finalQueryArgs,
    {
      skip: isDreamRateActive
    }
  )

  const count = data.length

  // If dream rate is active, there's no max limit
  const maxReached = isDreamRateActive ? false : count >= MAX_DREAM_RATE_COUNT

  return {
    isDreamRateActive,
    count,
    isFetching,
    data,
    maxReached
  }
}
